import { Button, Col, Form, Row, Space, Tag } from "antd";
import React, { useEffect, useRef, useState } from "react";
import ModalCustom from "../../components/ModalCustom/ModalCustom";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import SignatureCanvas from "react-signature-canvas";
import Notification from "../../components/Notification";
import InputFields from "../../components/InputFields/InputFields";
import { dataFormReason } from "../ContractList/config";
import contract from "../../api/contract";

// Dùng worker từ CDN, đúng version với lib đang import
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

function dataUrlToUint8Array(dataUrl) {
  const base64 = dataUrl.split(",")[1] || "";
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

const SIG_WIDTH = 260;
const SIG_HEIGHT = 90;

const Sign = ({ id, fileUrl, token, contractByCustomer }) => {
  const wrapperRef = useRef(null);
  const sigRef = useRef(null);
  const showSigned = useRef();

  const signedPdfDocRef = useRef(null);
  const signedCanvasRefs = useRef([]);
  const [signedNumPages, setSignedNumPages] = useState(0);
  const [loadingSignedPdf, setLoadingSignedPdf] = useState(false);

  const [loadingPdf, setLoadingPdf] = useState(false);

  const [reasonReject, setReasonReject] = useState(false);
  const [formReason] = Form.useForm();

  const handleOnviewSigned = async () => {
    try {
      setLoadingSignedPdf(true);
      signedCanvasRefs.current = [];
      setSignedNumPages(0);

      const res = await fetch(
        `${process.env.REACT_APP_SSO_URL}/contract-customer/contracts/preview?format=pdf`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.text();
      const parsedData = JSON.parse(data);
      const { base64Content } = parsedData;
      if (!base64Content) throw new Error("No PDF content");

      // base64 -> Uint8Array
      const binaryString = atob(base64Content);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);

      const loadingTask = pdfjsLib.getDocument({ data: bytes });
      const pdf = await loadingTask.promise;
      signedPdfDocRef.current = pdf;
      setSignedNumPages(pdf.numPages);

      // mở modal hiển thị PDF
      showSigned.current?.openModal();
    } catch (error) {
      console.error("Lỗi khi tải hợp đồng đã ký:", error);
      Notification("error", "Đã xảy ra lỗi khi xem hợp đồng đã ký");
    } finally {
      setLoadingSignedPdf(false);
    }
  };

  // mảng canvas cho từng trang PDF
  const pdfCanvasRefs = useRef([]);
  const pdfDocRef = useRef(null);

  const [numPages, setNumPages] = useState(0);

  // vị trí ô ký (trong hệ toạ độ của wrapper)
  const [sigPos, setSigPos] = useState({ x: 50, y: 50 });
  const [signaturePlaced, setSignaturePlaced] = useState(false); // true nếu user đã click đặt lại

  // ===== 1. Load PDF, lấy số trang =====
  useEffect(() => {
    if (!fileUrl) return;

    const loadPdf = async () => {
      try {
        setLoadingPdf(true);
        pdfCanvasRefs.current = []; // reset
        setNumPages(0);
        setSignaturePlaced(false); // mỗi file mới -> reset vị trí ký

        const pdfData = dataUrlToUint8Array(fileUrl);
        const loadingTask = pdfjsLib.getDocument({ data: pdfData });
        const pdf = await loadingTask.promise;

        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages); // sẽ trigger effect renderAllPages
      } catch (err) {
        console.error("Load PDF error:", err);
        setLoadingPdf(false);
      }
    };

    loadPdf();
  }, [fileUrl]);

  useEffect(() => {
    const renderSignedPages = async () => {
      const pdf = signedPdfDocRef.current;
      if (!pdf || signedNumPages === 0) return;

      try {
        for (let pageNumber = 1; pageNumber <= signedNumPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          const rotation = page.rotate || 0;
          const viewport = page.getViewport({
            scale: 1.2,
            rotation: -rotation,
          });

          const canvas = signedCanvasRefs.current[pageNumber - 1];
          if (!canvas) continue;

          const ctx = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      } catch (err) {
        console.error("Render signed pages error:", err);
      }
    };

    renderSignedPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedNumPages]);

  // ===== 2. Render tất cả các trang =====
  useEffect(() => {
    const renderAllPages = async () => {
      const pdf = pdfDocRef.current;
      if (!pdf || numPages === 0) return;

      try {
        for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);

          const rotation = page.rotate || 0;
          const viewport = page.getViewport({
            scale: 1.2,
            rotation: -rotation, // đưa về 0 độ hiển thị
          });

          const canvas = pdfCanvasRefs.current[pageNumber - 1];
          if (!canvas) continue;

          const ctx = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: ctx,
            viewport,
          }).promise;
        }

        // sau khi render xong hết -> đặt vị trí mặc định (nếu user chưa click)
        setTimeout(() => {
          if (!signaturePlaced) {
            positionSignatureDefault();
          }
        }, 0);
      } catch (err) {
        console.error("Render pages error:", err);
      } finally {
        setLoadingPdf(false);
      }
    };

    renderAllPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages]);

  // ===== 3. Đặt vị trí mặc định: góc phải phía dưới trang cuối (gần "Đại diện bên B") =====
  const positionSignatureDefault = () => {
    if (!wrapperRef.current || !pdfCanvasRefs.current.length) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const lastCanvas = pdfCanvasRefs.current[numPages - 1];
    if (!lastCanvas) return;

    const canvasRect = lastCanvas.getBoundingClientRect();

    // các tỉ lệ này bạn chỉnh để khớp chỗ "Đại diện bên B"
    const targetX =
      canvasRect.left - wrapperRect.left + canvasRect.width * 0.75;
    const targetY = canvasRect.top - wrapperRect.top + canvasRect.height * 0.8;

    setSigPos({
      x: targetX - SIG_WIDTH / 2,
      y: targetY - SIG_HEIGHT / 2,
    });
  };

  // ===== 4. Click ở đâu -> ô ký nhảy tới đó =====
  const handleWrapperClick = (e) => {
    if (!wrapperRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();

    const clickX = e.clientX - wrapperRect.left;
    const clickY = e.clientY - wrapperRect.top;

    setSigPos({
      x: clickX - SIG_WIDTH / 2,
      y: clickY - SIG_HEIGHT / 2,
    });
    setSignaturePlaced(true); // sau lần đầu click, không auto reposition nữa
  };

  // ===== 5. Gửi chữ ký =====
  const handleSendSignature = async () => {
    setLoadingPdf(true);
    if (!sigRef.current || sigRef.current.isEmpty()) {
      console.warn("Chưa có chữ ký");
      return;
    }

    // Lấy ảnh chữ ký dạng base64 (chuỗi không có tiền tố data:image/png;base64,)
    const canvas = sigRef.current.getCanvas();
    const dataUrl = canvas.toDataURL("image/png");
    const signatureBase64 = dataUrl.split(",")[1]; // chỉ lấy phần base64

    // Payload gửi BE
    const payload = {
      contractId: id, // truyền contractId đúng chỗ này
      signatureBase64: signatureBase64, // chuỗi base64 chữ ký
    };
    try {
      // const res = await contract.signedContractByCustomer(payload, token);
      const res = await fetch(
        `${process.env.REACT_APP_SSO_URL}/contract-customer/contracts/sign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // 👈 token ở đây
          },
          body: JSON.stringify(payload),
        }
      );
      Notification("success", "Đã ký thành công");
      handleClose();
      showSigned.current.openModal();
      // TODO: hiện message + đóng modal
      setLoadingPdf(false);
    } catch (err) {
      setLoadingPdf(false);
      console.error("Lỗi khi gửi chữ ký:", err);
    }
  };

  const handleClear = () => {
    sigRef.current?.clear();
  };

  const hanleRejectContract = async () => {
    try {
      const value = await formReason.validateFields();
      const data = {
        ...value,
      };
      const res = await contract.rejectSignedContract(value);
      formReason.resetFields();
      setReasonReject(false);
      handleClose();
      Notification("success", "Từ chối ký hợp đồng thành công");
    } catch (error) {
      console.log("🚀 ~ hanleRejectContract ~ error:", error);
      Notification("error", "Duyệt hợp đồng thất bại");
    }
  };
  const handleClose = () => {
    handleClear();
    contractByCustomer.current?.closeModal();
  };

  // ===== 6. Render JSX =====

  return (
    <div>
      <div
        style={{
          position: "relative",
          border: "1px solid #ccc",
          maxHeight: 600,
          overflow: "auto",
          cursor: "pointer",
        }}
      >
        {!fileUrl && <p>Không có hợp đồng để hiển thị.</p>}

        {fileUrl && (
          <div ref={wrapperRef} onClickCapture={handleWrapperClick}>
            {/* TẤT CẢ CÁC TRANG PDF */}
            {Array.from({ length: numPages }).map((_, index) => (
              <canvas
                key={index}
                ref={(el) => (pdfCanvasRefs.current[index] = el)}
                style={{
                  width: "100%",
                  display: "block",
                  marginBottom: 8,
                }}
              />
            ))}

            {/* Ô ký – mặc định ở trang cuối bên phải, click đâu nhảy tới đó */}
            <SignatureCanvas
              ref={sigRef}
              canvasProps={{
                width: SIG_WIDTH,
                height: SIG_HEIGHT,
                style: {
                  position: "absolute",
                  left: sigPos.x,
                  top: sigPos.y,
                  border: "1px solid #999",
                  background: "rgba(255,255,255,0.9)",
                  cursor: "crosshair",
                },
              }}
            />

            {loadingPdf && (
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  padding: "4px 8px",
                  background: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  borderRadius: 4,
                }}
              >
                Đang tải PDF...
              </div>
            )}
          </div>
        )}
      </div>
      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col span={6}>
          <Row justify="start" style={{ gap: 4 }}>
            <Button danger onClick={handleClose}>
              HỦY
            </Button>
          </Row>
        </Col>
        <Col span={18}>
          <Row justify="end" gutter={8}>
            <Col>
              <Space direction="horizontal" size="middle">
                {reasonReject ? (
                  <Space style={{ width: "100%" }}>
                    <Form form={formReason}>
                      <Row gutter={[16, 8]}>
                        <InputFields data={dataFormReason} />
                      </Row>
                    </Form>
                    <Button type="primary" onClick={hanleRejectContract}>
                      Gửi
                    </Button>
                    <Button danger onClick={() => setReasonReject(false)}>
                      Hủy
                    </Button>
                  </Space>
                ) : (
                  <Button danger onClick={() => setReasonReject(true)}>
                    Từ chối ký
                  </Button>
                )}
              </Space>
            </Col>
            <Col>
              <Button type="default" onClick={handleClear}>
                Ký lại
              </Button>
            </Col>
            <Col>
              <Button type="primary" onClick={handleSendSignature}>
                Gửi chữ ký
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <ModalCustom ref={showSigned} title="Hợp đồng cá nhân đã ký">
        <div style={{ width: "100%", maxHeight: "80vh", overflow: "auto" }}>
          <Row>
            <Tag bordered={false} color={"green"}>
              Đã ký thành công
            </Tag>
            <Col>
              <Button type="default" onClick={handleOnviewSigned}>
                Xem hợp đồng đã ký
              </Button>
            </Col>
          </Row>
          {loadingSignedPdf && (
            <div style={{ padding: 8 }}>Đang tải hợp đồng đã ký...</div>
          )}

          {signedNumPages > 0 && (
            <div>
              {Array.from({ length: signedNumPages }).map((_, index) => (
                <canvas
                  key={index}
                  ref={(el) => (signedCanvasRefs.current[index] = el)}
                  style={{ width: "100%", display: "block", marginBottom: 8 }}
                />
              ))}
            </div>
          )}

          {/* Nút đóng modal (nếu ModalCustom không có sẵn) */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 8,
            }}
          >
            <Button onClick={() => showSigned.current?.closeModal?.()}>
              ĐÓNG
            </Button>
          </div>
        </div>
      </ModalCustom>
    </div>
  );
};

export default Sign;
